import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, NAV_LINKS, ASSETS } from "@/lib/siteData";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 1.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-[#060606]/85 backdrop-blur-xl border-b border-[var(--line)] py-3" : "py-5 border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={ASSETS.logo} alt="Advocate" className="h-9 md:h-10 w-auto object-contain transition-transform duration-500 group-hover:-translate-y-0.5" />
          <div className="leading-tight border-l border-[var(--line)] pl-3 hidden sm:block">
            <div className="font-serif-display text-base md:text-lg tracking-tight leading-none">S. Kr Sinha</div>
            <div className="font-mono-plex text-[9px] uppercase tracking-[0.22em] text-[var(--muted)]">Advocate · Purnia</div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-underline font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[var(--off)]/80 hover:text-[var(--off)] pb-1"
            >
              {l.label}
            </a>
          ))}
          <a
            href={`tel:${CONTACT.phone}`}
            data-testid="nav-call-btn"
            className="flex items-center gap-2 bg-[var(--off)] text-[#060606] px-4 py-2 text-[11px] font-mono-plex uppercase tracking-[0.18em] hover:bg-white transition-colors duration-300"
          >
            <Phone size={13} /> Call
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[var(--off)]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#060606] border-t border-[var(--line)] mt-3"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif-display text-2xl"
                >
                  {l.label}
                </a>
              ))}
              <a href={`tel:${CONTACT.phone}`} className="mt-2 bg-[var(--off)] text-[#060606] px-4 py-3 text-center font-mono-plex text-xs uppercase tracking-[0.2em]">
                Call {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
